import { clsx, type ClassValue } from "clsx";
import { twMerge } from "tailwind-merge";
import { format, formatDistanceToNow } from "date-fns";
import { es } from "date-fns/locale";

/**
 * Merge Tailwind classes with clsx
 */
export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

/**
 * Format date to Spanish locale.
 *
 * Acepta Date, string ISO o timestamp numérico (ms desde epoch).
 *
 * - Si el formato pide solo fecha (sin tokens de hora) → reconstruye con
 *   componentes UTC para evitar el "day-shift" en timezones UTC negativos
 *   (fechas guardadas como UTC midnight).
 * - Si el formato incluye hora (HH/mm/ss/h) → usa la fecha tal cual para
 *   no perder la hora local del valor original.
 */
export function formatDate(
  date: Date | string | number,
  formatStr: string = "dd/MM/yyyy",
): string {
  const d =
    typeof date === "string" || typeof date === "number"
      ? new Date(date)
      : date;

  if (!(d instanceof Date) || Number.isNaN(d.getTime())) {
    return "";
  }

  const hasTimeToken = /[Hhmsa]/.test(formatStr);
  const target = hasTimeToken
    ? d
    : new Date(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate());

  return format(target, formatStr, { locale: es });
}

/**
 * Format relative time in Spanish
 */
export function formatRelativeTime(date: Date | string): string {
  const d = typeof date === "string" ? new Date(date) : date;
  return formatDistanceToNow(d, { addSuffix: true, locale: es });
}

/**
 * Format currency (COP)
 */
export function formatCurrency(amount: number): string {
  return new Intl.NumberFormat("es-CO", {
    style: "currency",
    currency: "COP",
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(amount);
}

/**
 * Calculate age from birth date — UTC-safe.
 * Birth dates stored as UTC midnight are reconstructed using UTC components
 * before comparison so they display correctly in UTC-5 (Colombia).
 */
export function calculateAge(birthDate: Date | string): number {
  const today = new Date();
  // Parse string as noon to keep local day = UTC day across UTC±11 timezones
  const birth =
    typeof birthDate === "string"
      ? new Date(birthDate + "T12:00:00")
      : new Date(
          birthDate.getUTCFullYear(),
          birthDate.getUTCMonth(),
          birthDate.getUTCDate(),
        );
  let age = today.getFullYear() - birth.getFullYear();
  const monthDiff = today.getMonth() - birth.getMonth();
  if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birth.getDate())) {
    age--;
  }
  return age;
}

/**
 * Get club type by age
 */
export function getClubTypeByAge(
  age: number,
): "aventureros" | "conquistadores" | "guias_mayores" | null {
  if (age >= 3 && age <= 9) return "aventureros";
  if (age >= 10 && age <= 15) return "conquistadores";
  if (age >= 16) return "guias_mayores";
  return null;
}

/**
 * Devuelve TODAS las secciones a las que puede pertenecer una edad, con
 * solapamiento en los años frontera (10 y 15). Esto permite que un director
 * inscriba, por ejemplo, a un niño de 10 años en Aventureros aunque el default
 * primario sea Conquistadores.
 *
 * Casos límite:
 *  - 10 años → ["aventureros", "conquistadores"] (puede terminar su ciclo aventurero)
 *  - 15 años → ["conquistadores", "guias_mayores"]
 */
export function getAllowedClubTypesByAge(
  age: number,
): ("aventureros" | "conquistadores" | "guias_mayores")[] {
  const types: ("aventureros" | "conquistadores" | "guias_mayores")[] = [];
  if (age >= 3 && age <= 10) types.push("aventureros");
  if (age >= 10 && age <= 15) types.push("conquistadores");
  if (age >= 15) types.push("guias_mayores");
  return types;
}

/**
 * True si una fecha de nacimiento es IMPLAUSIBLE: futura, o produce una edad
 * fuera de [1, 120]. Bloquea errores de digitación de año (ej. 2026 → edad 0).
 * Acepta Date, Timestamp ({toDate}) o string "YYYY-MM-DD". Una fecha ausente
 * NO se considera implausible (eso lo valida "requerido" por separado).
 */
export function isImplausibleBirthDate(
  birthDate: Date | { toDate: () => Date } | string | null | undefined,
): boolean {
  if (!birthDate) return false;
  let d: Date;
  if (typeof birthDate === "string") d = new Date(birthDate + "T12:00:00");
  else if (birthDate instanceof Date) d = birthDate;
  else if (typeof (birthDate as { toDate?: unknown }).toDate === "function")
    d = (birthDate as { toDate: () => Date }).toDate();
  else return false;
  if (isNaN(d.getTime())) return true;
  if (d.getTime() > Date.now()) return true; // futura
  const age = calculateAge(typeof birthDate === "string" ? birthDate : d);
  return age < 1 || age > 120;
}

/**
 * Returns today's date as "YYYY-MM-DD" using the LOCAL timezone.
 * Use instead of new Date().toISOString().split("T")[0] which gives UTC date
 * and can return tomorrow's date after 7pm in Colombia (UTC-5).
 */
export function todayLocalDateString(): string {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

/**
 * Derives ClubType from birth date.
 * Single source of truth — memberType is NEVER manually selected.
 */
export function deriveSectionFromBirthDate(
  birthDate: Date | { toDate: () => Date },
): "aventureros" | "conquistadores" | "guias_mayores" | null {
  const d = birthDate instanceof Date ? birthDate : birthDate.toDate();
  const age = calculateAge(d);
  return getClubTypeByAge(age);
}

/**
 * Determina la sección al inscribir en un club específico, respetando el
 * solapamiento de edades y las secciones ACTIVAS del club. Preferencia:
 *   1) sección primaria por edad si está activa en el club
 *   2) sección permitida (solapamiento) que esté activa en el club
 *   3) fallback: sección primaria por edad (aunque no esté activa)
 *
 * Ejemplo: niño de 10 años inscrito en un club que sólo tiene la sección
 * de Aventureros activa → devuelve "aventureros" (aunque el default por
 * edad sea "conquistadores").
 */
export function resolveSectionForClub(
  birthDate: Date | { toDate: () => Date },
  club: { sections?: Record<string, { active: boolean }>; type?: string },
): "aventureros" | "conquistadores" | "guias_mayores" | null {
  const d = birthDate instanceof Date ? birthDate : birthDate.toDate();
  const age = calculateAge(d);
  const primary = getClubTypeByAge(age);
  const allowed = getAllowedClubTypesByAge(age);
  const active = getActiveSections(club) as (
    | "aventureros"
    | "conquistadores"
    | "guias_mayores"
  )[];
  if (primary && allowed.includes(primary) && active.includes(primary)) {
    return primary;
  }
  const fallback = allowed.find((s) => active.includes(s));
  return fallback ?? primary;
}

/**
 * Get the active sections of a club as an array of ClubType.
 * Always returned in canonical order: aventureros → conquistadores → guias_mayores.
 */
export function getActiveSections(club: {
  sections?: Record<string, { active: boolean }>;
  type?: string;
}): string[] {
  const ORDER = ["aventureros", "conquistadores", "guias_mayores"];
  if (club.sections) {
    const active = Object.entries(club.sections)
      .filter(([, config]) => config.active)
      .map(([key]) => key);
    return active.sort(
      (a, b) =>
        (ORDER.indexOf(a) === -1 ? 99 : ORDER.indexOf(a)) -
        (ORDER.indexOf(b) === -1 ? 99 : ORDER.indexOf(b)),
    );
  }
  // Fallback for legacy clubs that only have `type`
  if (club.type) return [club.type];
  return [];
}

/**
 * Get club type label
 */
export function getClubTypeLabel(type: string): string {
  const labels: Record<string, string> = {
    aventureros: "Aventureros",
    conquistadores: "Conquistadores",
    guias_mayores: "Guías Mayores",
  };
  return labels[type] || type;
}

/**
 * Get status badge color
 */
export function getStatusColor(status: string): string {
  const colors: Record<string, string> = {
    active: "badge-green",
    inactive: "badge-gray",
    pending: "badge-yellow",
    transferred: "badge-blue",
    cancelled: "badge-red",
    paid: "badge-green",
    partial: "badge-yellow",
    overdue: "badge-red",
  };
  return colors[status] || "badge-gray";
}

/**
 * Get status label in Spanish
 */
export function getStatusLabel(status: string): string {
  const labels: Record<string, string> = {
    active: "Activo",
    inactive: "Inactivo",
    pending: "Pendiente",
    transferred: "Trasladado",
    cancelled: "Cancelado",
    paid: "Pagado",
    partial: "Parcial",
    overdue: "Vencido",
    approved: "Aprobado",
    rejected: "Rechazado",
    confirmed: "Confirmado",
    draft: "Borrador",
    published: "Publicado",
    closed: "Cerrado",
  };
  return labels[status] || status;
}

/**
 * Generate a random ID
 */
export function generateId(prefix: string = ""): string {
  const timestamp = Date.now().toString(36);
  const randomPart = Math.random().toString(36).substring(2, 8);
  return prefix
    ? `${prefix}_${timestamp}${randomPart}`
    : `${timestamp}${randomPart}`;
}

/**
 * Valida que un parámetro ?redirect= sea una ruta interna segura.
 * Previene Open Redirect: rechaza URLs absolutas y protocol-relative URLs.
 */
export function isSafeRedirect(url: string): boolean {
  if (!url) return false;
  // Debe empezar con / pero NO con // (protocol-relative) ni ://
  return url.startsWith("/") && !url.startsWith("//") && !url.includes("://");
}

/**
 * Slugify a string
 */
export function slugify(text: string): string {
  return text
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[^a-z0-9]+/g, "_")
    .replace(/^_+|_+$/g, "");
}

/**
 * Truncate text
 */
export function truncate(text: string, maxLength: number): string {
  if (text.length <= maxLength) return text;
  return `${text.substring(0, maxLength)}...`;
}

/**
 * Debounce function
 */
export function debounce<T extends (...args: any[]) => any>(
  func: T,
  wait: number,
): (...args: Parameters<T>) => void {
  let timeout: NodeJS.Timeout;

  return (...args: Parameters<T>) => {
    clearTimeout(timeout);
    timeout = setTimeout(() => func(...args), wait);
  };
}

/**
 * Mapeo de categorías a carpetas de imágenes
 */
const CATEGORY_FOLDER_MAP: Record<string, string> = {
  // Nombres originales del script populate-specialties.ts
  "actividades-agropecuarias": "agropecuarias",
  "actividades-recreacionales": "recreacion",
  "actividades-vocacionales": "vocacional",
  "artes-manuales": "artes-manuales",
  "artes-domesticas": "artes-domesticas",
  "crecimiento-espiritual": "crecimiento-espiritual",
  "estudio-naturaleza": "naturaleza",
  "salud-ciencia": "salud-ciencia",
  "servicios-comunitarios": "adra", // Servicios comunitarios usa imágenes de ADRA
  adra: "adra",
  // Variantes posibles
  agropecuarias: "agropecuarias",
  recreacion: "recreacion",
  vocacional: "vocacional",
  naturaleza: "naturaleza",
};

/**
 * Mapeo de especialidades que están en carpetas diferentes a su categoría
 * { nombreSlug: { folder: 'carpeta', file: 'nombre-archivo' } }
 */
const SPECIALTY_OVERRIDES: Record<string, { folder: string; file: string }> = {
  fotografia: { folder: "artes-manuales", file: "fotografia" },
  electricidad: { folder: "vocacional", file: "electricidad" },
  "natacion-avanzado": { folder: "recreacion", file: "natacion-i-avanzado" },
  espeleologia: { folder: "recreacion", file: "espeleologia-deportiva" },
  "espeleologia-avanzado": {
    folder: "recreacion",
    file: "espeleologia-deportiva-avanzado",
  },
};

/**
 * Obtiene la URL de la imagen local de una especialidad
 * Intenta múltiples formatos de nombre
 */
export function getSpecialtyImageUrl(
  name: string,
  categorySlug?: string,
): string {
  // Slugificar el nombre de la especialidad
  const nameSlug = name
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "") // Remover acentos
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");

  // Verificar si hay un override para esta especialidad
  const override = SPECIALTY_OVERRIDES[nameSlug];
  if (override) {
    return `/images/specialties/${override.folder}/${override.file}.png`;
  }

  // Determinar la carpeta de categoría
  const folder = categorySlug
    ? CATEGORY_FOLDER_MAP[categorySlug] || categorySlug
    : "";

  // Si tenemos carpeta, devolver la ruta
  if (folder) {
    return `/images/specialties/${folder}/${nameSlug}.png`;
  }

  // Fallback al placeholder
  return "/images/specialties/placeholder.png";
}

/**
 * Etiquetas amigables para categorías de especialidades
 */
export const SPECIALTY_CATEGORY_LABELS: Record<string, string> = {
  "actividades-agropecuarias": "Actividades Agropecuarias",
  "actividades-recreacionales": "Actividades Recreacionales",
  "actividades-vocacionales": "Actividades Vocacionales",
  "artes-manuales": "Artes y Habilidades Manuales",
  "artes-domesticas": "Artes Domésticas",
  "crecimiento-espiritual": "Crecimiento Espiritual",
  "estudio-naturaleza": "Estudio de la Naturaleza",
  "salud-ciencia": "Salud y Ciencia",
  "servicios-comunitarios": "Servicios Comunitarios",
  adra: "ADRA",
};

/**
 * Íconos y colores para categorías de especialidades
 */
export const SPECIALTY_CATEGORY_STYLES: Record<
  string,
  { color: string; bgColor: string; icon: string }
> = {
  "actividades-agropecuarias": {
    color: "text-green-700",
    bgColor: "bg-green-100",
    icon: "🌾",
  },
  "actividades-recreacionales": {
    color: "text-blue-700",
    bgColor: "bg-blue-100",
    icon: "⚽",
  },
  "actividades-vocacionales": {
    color: "text-orange-700",
    bgColor: "bg-orange-100",
    icon: "🔧",
  },
  "artes-manuales": {
    color: "text-pink-700",
    bgColor: "bg-pink-100",
    icon: "🎨",
  },
  "artes-domesticas": {
    color: "text-amber-700",
    bgColor: "bg-amber-100",
    icon: "🍳",
  },
  "crecimiento-espiritual": {
    color: "text-purple-700",
    bgColor: "bg-purple-100",
    icon: "✝️",
  },
  "estudio-naturaleza": {
    color: "text-emerald-700",
    bgColor: "bg-emerald-100",
    icon: "🌿",
  },
  "salud-ciencia": { color: "text-red-700", bgColor: "bg-red-100", icon: "🏥" },
  "servicios-comunitarios": {
    color: "text-cyan-700",
    bgColor: "bg-cyan-100",
    icon: "🏘️",
  },
  adra: { color: "text-teal-700", bgColor: "bg-teal-100", icon: "🤝" },
};

// ==================== NORMALIZACIÓN DE TEXTO ====================

/**
 * Capitaliza la primera letra de cada palabra.
 * Maneja correctamente acentos y caracteres especiales en español.
 *
 * @example
 * capitalizeWords("DIEGO LOPEZ") // "Diego Lopez"
 * capitalizeWords("diego alejandro") // "Diego Alejandro"
 * capitalizeWords("maría josé") // "María José"
 */
export function capitalizeWords(text: string): string {
  if (!text) return "";

  return text
    .toLowerCase()
    .split(" ")
    .map((word) => {
      if (word.length === 0) return "";
      // Capitalizar primera letra (maneja acentos correctamente)
      return word.charAt(0).toUpperCase() + word.slice(1);
    })
    .join(" ")
    .trim();
}

/**
 * Normaliza un nombre propio (nombre o apellido).
 * Maneja casos especiales como "de", "del", "la", "los", "van", "von", etc.
 *
 * @example
 * normalizeName("DIEGO ALEJANDRO") // "Diego Alejandro"
 * normalizeName("maria del carmen") // "Maria del Carmen"
 * normalizeName("van der berg") // "Van der Berg"
 */
export function normalizeName(text: string): string {
  if (!text) return "";

  // Palabras que deben permanecer en minúsculas (excepto al inicio)
  const lowercaseWords = new Set([
    "de",
    "del",
    "la",
    "las",
    "los",
    "el",
    "y",
    "e",
    "o",
    "u",
    "van",
    "von",
    "der",
    "den",
    "da",
    "do",
    "dos",
  ]);

  const words = text.toLowerCase().trim().split(/\s+/);

  return words
    .map((word, index) => {
      if (word.length === 0) return "";

      // Primera palabra siempre capitalizada
      if (index === 0) {
        return word.charAt(0).toUpperCase() + word.slice(1);
      }

      // Palabras especiales en minúsculas
      if (lowercaseWords.has(word)) {
        return word;
      }

      // Resto de palabras capitalizadas
      return word.charAt(0).toUpperCase() + word.slice(1);
    })
    .join(" ");
}

/**
 * Normaliza un nombre completo (nombre + apellido).
 *
 * @example
 * normalizeFullName("DIEGO", "LOPEZ CAMACHO") // "Diego Lopez Camacho"
 * normalizeFullName("MARIA DEL CARMEN", "DE LA ROSA") // "Maria del Carmen de la Rosa"
 */
export function normalizeFullName(firstName: string, lastName: string): string {
  const normalizedFirst = normalizeName(firstName);
  const normalizedLast = normalizeName(lastName);
  return `${normalizedFirst} ${normalizedLast}`.trim();
}

/**
 * Normaliza un email (siempre en minúsculas).
 */
export function normalizeEmail(email: string): string {
  if (!email) return "";
  return email.toLowerCase().trim();
}

/**
 * Normaliza un número de teléfono (solo dígitos y formato).
 * Elimina espacios y caracteres especiales excepto + al inicio.
 *
 * @example
 * normalizePhone("  +57 300 123 4567  ") // "+573001234567"
 * normalizePhone("300-123-4567") // "3001234567"
 */
export function normalizePhone(phone: string): string {
  if (!phone) return "";

  const trimmed = phone.trim();
  const hasPlus = trimmed.startsWith("+");

  // Eliminar todo excepto dígitos
  const digits = trimmed.replace(/\D/g, "");

  return hasPlus ? `+${digits}` : digits;
}

/**
 * Normaliza un número de documento de identidad.
 * Elimina espacios y puntos, convierte a mayúsculas.
 *
 * @example
 * normalizeDocumentNumber("1.023.456.789") // "1023456789"
 * normalizeDocumentNumber(" AB 123456 ") // "AB123456"
 */
export function normalizeDocumentNumber(docNumber: string): string {
  if (!docNumber) return "";
  return docNumber
    .replace(/[\s.,-]/g, "")
    .toUpperCase()
    .trim();
}

/**
 * Normaliza una dirección.
 * Capitaliza palabras pero mantiene números y abreviaciones.
 *
 * @example
 * normalizeAddress("CALLE 123 # 45-67") // "Calle 123 # 45-67"
 * normalizeAddress("carrera 10 sur # 20-30") // "Carrera 10 Sur # 20-30"
 */
export function normalizeAddress(address: string): string {
  if (!address) return "";

  // Palabras que son abreviaciones y deben mantenerse
  const abbreviations = new Set(["#", "no", "no."]);

  return address
    .toLowerCase()
    .trim()
    .split(/\s+/)
    .map((word) => {
      if (abbreviations.has(word)) return word;
      // Si es un número o contiene guiones, mantener como está
      if (/^\d+$/.test(word) || /^\d+-\d+/.test(word)) return word;
      return word.charAt(0).toUpperCase() + word.slice(1);
    })
    .join(" ");
}

/**
 * Normaliza una ciudad.
 *
 * @example
 * normalizeCity("BOGOTÁ D.C.") // "Bogotá D.C."
 * normalizeCity("santa marta") // "Santa Marta"
 */
export function normalizeCity(city: string): string {
  if (!city) return "";
  return capitalizeWords(city);
}

/**
 * Objeto con todas las funciones de normalización para uso fácil.
 */
export const normalize = {
  name: normalizeName,
  fullName: normalizeFullName,
  words: capitalizeWords,
  email: normalizeEmail,
  phone: normalizePhone,
  documentNumber: normalizeDocumentNumber,
  address: normalizeAddress,
  city: normalizeCity,
};
